import { useEffect, useState } from "react";
import { api } from "@/lib/api";

export const HISTORY_CATEGORY = { ga_weather: "Georgia Weather History", us_weather: "U.S. Weather History", ga_history: "Georgia History" };

export function useHistoryFacts() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get("/history/today").then((r) => setData(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 600_000);
    return () => clearInterval(t);
  }, []);
  return data;
}

const PAPER_BG = "radial-gradient(ellipse at 50% 40%, #efe5cf 0%, #e6d9bd 55%, #cdbb97 100%)";
const GRAIN = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.2 0 0 0 0 0.15 0 0 0 0 0.1 0 0 0 0.35 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")";
const INK = "#261e16";
const RULE = "#5c4a34";
const STAMP = "#9e2420";
const SERIF = "'Liberation Serif', 'Times New Roman', Georgia, serif";

// Archival newspaper slide — cycles one fact per category across the slide's duration
export function HistorySlide({ facts, perMs = 8000 }) {
  const items = facts?.items || [];
  const [k, setK] = useState(0);
  useEffect(() => {
    if (items.length < 2) return undefined;
    const t = setInterval(() => setK((n) => (n + 1) % items.length), Math.max(4000, perMs));
    return () => clearInterval(t);
  }, [items.length, perMs]);
  const it = items[Math.min(k, Math.max(items.length - 1, 0))];
  if (!it) return null;
  return (
    <div data-testid="history-slide" className="relative h-full w-full overflow-hidden" style={{ background: PAPER_BG, color: INK }}>
      <div className="absolute inset-0 opacity-60 mix-blend-multiply" style={{ backgroundImage: GRAIN }} />
      <div className="absolute inset-0" style={{ boxShadow: "inset 0 0 220px rgba(80,55,25,0.45)" }} />
      <div className="relative flex h-full w-full flex-col px-[90px] pb-[80px] pt-[44px]">
        <div className="border-y-[5px] py-1" style={{ borderColor: INK }}>
          <div className="flex items-center justify-between border-t-2 pt-2" style={{ borderColor: INK }}>
            <div className="font-oswald text-[22px] font-bold uppercase tracking-[0.4em]" style={{ color: RULE }}>Georgia Weather Network · Archives</div>
            <div className="font-oswald text-[24px] font-black uppercase tracking-[0.4em]" style={{ color: STAMP }} data-testid="history-slide-date">{facts.date_label}</div>
          </div>
          <div className="text-center text-[116px] font-bold leading-[1.05]" style={{ fontFamily: SERIF }}>On This Day</div>
        </div>

        <div key={`${it.category}-${k}`} className="alert-wipe mt-10 flex flex-1 items-start gap-14">
          <div className="shrink-0">
            <div className="font-oswald text-[24px] font-bold uppercase tracking-[0.35em]" style={{ color: STAMP }} data-testid="history-slide-category">{HISTORY_CATEGORY[it.category] || "History"}</div>
            <div className="mt-1 text-[220px] font-bold leading-[0.95] tracking-tight" style={{ fontFamily: SERIF }} data-testid="history-slide-year">{it.year || "—"}</div>
            <div className="mt-3 h-[3px] w-[180px]" style={{ background: RULE }} />
          </div>
          <div className="min-w-0 flex-1 pt-14">
            <div className="text-[64px] font-bold leading-[1.05]" style={{ fontFamily: SERIF }} data-testid="history-slide-title">{it.title}</div>
            <div className="mt-6 max-w-[1150px] text-[40px] leading-[1.35]" style={{ fontFamily: SERIF }} data-testid="history-slide-text">{it.text}</div>
          </div>
        </div>

        <div className="flex items-center justify-between border-t-[3px] pt-4" style={{ borderColor: INK }}>
          <div className="flex items-center gap-3 font-oswald text-[20px] font-bold uppercase tracking-[0.25em]">
            {items.map((x, i) => (
              <span key={x.category} className="rounded-sm px-3 py-1" style={{ background: i === k ? INK : "transparent", color: i === k ? "#efe5cf" : RULE, border: `2px solid ${i === k ? INK : RULE}` }}>{HISTORY_CATEGORY[x.category]}</span>
            ))}
          </div>
          <div className="font-oswald text-[20px] font-bold uppercase tracking-[0.3em]" style={{ color: STAMP }}>{facts.status === "approved" ? "Facts reviewed by our team" : "From the archives"}</div>
        </div>
      </div>
    </div>
  );
}
